import { Check, ChevronDown, Search, X } from "lucide-react";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { Button } from "@/v2/ui/primitives/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/v2/ui/primitives/popover";
import { ScrollArea } from "@/v2/ui/primitives/scroll-area";

export interface SearchableSelectOption {
  value: string;
  label: string;
  icon?: string;
  metadata?: Record<string, unknown>;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /** Enable "All" option that clears the selection */
  allowAll?: boolean;
  /** Label for the "All" option (default: "All") */
  allLabel?: string;
  /** Custom render for option content (icon + label) */
  renderOption?: (option: SearchableSelectOption) => ReactNode;
  /** Custom render for selected value display */
  renderValue?: (option: SearchableSelectOption) => ReactNode;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = strings("placeholders.select"),
  searchPlaceholder,
  emptyMessage,
  disabled = false,
  className,
  allowAll = false,
  allLabel = strings("common.all"),
  renderOption,
  renderValue,
}: SearchableSelectProps) {
  const resolvedSearchPlaceholder =
    searchPlaceholder ?? strings("common.search");
  const resolvedEmptyMessage = emptyMessage ?? strings("common.noResultsFound");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  );

  // Default option renderer with icon support
  const defaultRenderOption = (option: SearchableSelectOption) => (
    <span className="tw-flex tw-items-center tw-gap-2 tw-truncate">
      {option.icon && (
        <img
          src={option.icon}
          alt=""
          className="tw-w-5 tw-h-5 tw-rounded tw-flex-shrink-0"
        />
      )}
      <span className="tw-truncate">{option.label}</span>
    </span>
  );

  const optionRenderer = renderOption || defaultRenderOption;
  const valueRenderer = renderValue || defaultRenderOption;

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const searchLower = search.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(searchLower) ||
        opt.value.toLowerCase().includes(searchLower)
    );
  }, [options, search]);

  const handleSelect = (selectedValue: string) => {
    onChange?.(selectedValue);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild={true}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "tw-w-full tw-justify-between tw-font-normal tw-text-foreground",
            !selectedOption && "tw-text-muted-foreground",
            className
          )}
        >
          <span className="tw-truncate">
            {selectedOption
              ? valueRenderer(selectedOption)
              : allowAll && !value
                ? allLabel
                : placeholder}
          </span>
          <ChevronDown className="tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="tw-w-[--radix-popover-trigger-width] tw-p-0"
        align="start"
      >
        <div className="tw-flex tw-items-center tw-border-b tw-px-3 tw-py-2">
          <Search className="tw-mr-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
          <input
            placeholder={resolvedSearchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="tw-flex tw-h-8 tw-w-full tw-bg-transparent tw-text-sm tw-outline-none placeholder:tw-text-muted-foreground"
          />
          {search && (
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-6 tw-w-6"
              onClick={() => setSearch("")}
            >
              <X className="tw-h-3 tw-w-3" />
            </Button>
          )}
        </div>
        <ScrollArea className="tw-h-[300px]">
          <div className="tw-p-1">
            {allowAll && (
              <button
                onClick={() => handleSelect("")}
                className={cn(
                  "tw-relative tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded-sm tw-py-2 tw-px-3 tw-text-sm tw-outline-none hover:tw-bg-accent hover:tw-text-accent-foreground",
                  !value && "tw-bg-accent tw-text-accent-foreground"
                )}
              >
                <Check
                  className={cn(
                    "tw-mr-2 tw-h-4 tw-w-4 tw-flex-shrink-0",
                    !value ? "tw-opacity-100" : "tw-opacity-0"
                  )}
                />
                {allLabel}
              </button>
            )}
            {filteredOptions.length === 0 ? (
              <div className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
                {resolvedEmptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    "tw-relative tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded-sm tw-py-2 tw-px-3 tw-text-sm tw-outline-none hover:tw-bg-accent hover:tw-text-accent-foreground",
                    value === option.value &&
                      "tw-bg-accent tw-text-accent-foreground"
                  )}
                >
                  <Check
                    className={cn(
                      "tw-mr-2 tw-h-4 tw-w-4 tw-flex-shrink-0",
                      value === option.value ? "tw-opacity-100" : "tw-opacity-0"
                    )}
                  />
                  {optionRenderer(option)}
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
